
import React, { useEffect, useState } from 'react';
import qs from 'qs';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';

import { ContentLayout } from 'components/Layout';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { paths } from 'routes';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';
import { useParams } from 'react-router-dom';
import { Search } from 'components/Search';
import { useDebouncedCallback } from 'use-debounce';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';
import { NotFound } from 'components/NotFound/NotFound';
import { useCompany } from '../api/getCompanyByRelatedID';
import { useGetByID } from '../api/getById';
import { useAuth } from 'lib/auth';

import { useGetSupplierFiles } from '../api/getSupplierFiles';
import { FileListItem } from '../components/FileListItem';
import { downloadFile, donwloadFileFromURL } from '../api/downloadFile';
import { FileUploadModal } from '../components/SupplierFileUploadModal';
import { useFilePreview } from '../api/previewFile';
import { PDFViewer } from 'components/PDFViewer/PDFViewer';
import { useRemoveFile } from '../api/removeFile';
import { ImageViewer } from 'components/ImageViewer/ImageViewer';

export const SuppliersFiles = () => {
  const { account } = useAuth();
  const { id: dpsId, dcId } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');
  const [markedForDelete, setMarkedForDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentFilePreviewId, setCurrentFilePreviewId] = useState(null);
  const [markedForEdit, setMarkedForEdit] = useState(null);

  const { data: company } = useCompany(dpsId, dcId);
  const { data: dps } = useGetByID(dpsId);

  const {
    data: { files, total },
    refetch: refetchFiles,
  } = useGetSupplierFiles(dpsId, dcId, { search, page: page - 1, limit: pageSize });
  const {
    data: { url, name, FileExt, MimeType },
  } = useFilePreview(dpsId, currentFilePreviewId, dcId);

  const onFileRemoval = () => {
    refetchFiles();
    setMarkedForDelete(null);
  };

  const handlePageChange = (_, page) => setPage(page);

  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    if (!loading) setLoading(true);
    debouncedSearchChange(search);
  };

  const handleMutation = () => {
    refetchFiles();
    if (markedForEdit) setMarkedForEdit(null);
  };

  const { mutateAsync: removeFile } = useRemoveFile(onFileRemoval);
  useRefetch();

  return (
    <ContentLayout>
      <Stack height="100%">
        <BreadCrumbs
          items={[
            { name: 'Dynamic Purchasing Systems', to: paths.dps },
            {
              name: dps?.fa_name ?? 'Details View',
              to: `${paths.dps}${dpsId}`,
            },
            {
              name: 'Suppliers',
              to: `${paths.dps}${dpsId}?${qs.stringify({ tab: 'suppliers' })}`,
            },
            { name: company?.Company?.Name },
          ]}
        />

        <Stack
          direction="row"
          height={75}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h3">{company?.Company?.Name + " - Files" || ''}</Typography>
        </Stack>

        <Stack
          px={4}
          py={2}
          bgcolor="background.paper"
          borderTop="1px solid"
          borderColor="border.main"
          gap={3}
          height="100%"
        >
          <ImageViewer
            url={url}
            open={!!currentFilePreviewId && MimeType.includes('image/')}
            close={() => setCurrentFilePreviewId(null)}
            handleDownload={() => donwloadFileFromURL(url, name + FileExt)}
          />

          <PDFViewer
            url={url}
            open={!!currentFilePreviewId && MimeType.includes('application/pdf')}
            close={() => setCurrentFilePreviewId(null)}
            handleDownload={() => donwloadFileFromURL(url, name + FileExt)}
          />
          <DeleteConfirmationModal
            title={'Remove file'}
            markedForDelete={markedForDelete}
            mutateAsync={() => removeFile({ dpsID: dpsId, dcID: dcId, fileId: markedForDelete?.itemId })}
          >
            <Typography variant="body1" fontWeight={500}>
              Please confirm the removal of the file{' '}
              <span style={{ fontWeight: 'bold' }}>{markedForDelete?.name}.</span>
            </Typography>
          </DeleteConfirmationModal>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            width={1}
          >
            <Search handleSearchChange={handleSearchChange} sx={{minWidth:200, width: 350}} />
            {account.super_admin && (
              <>
              <Box sx={{minWidth: 150, width: 200}}>
                <FileUploadModal handleSuccess={handleMutation} markedForEdit={markedForEdit} />
              </Box>
            </>
            )}
          </Stack>

          <Divider />

          <Stack gap={1} flex={1}>
            {files?.length === 0 && (
              <NotFound
                title="No Files found"
                caption={
                  search
                    ? 'Please try another search term'
                    : account.super_admin ? 'Click the "Add file" button to begin' : ''
                }
                wrapperSx={{ mt: 19 }}
              />
            )}

            {files?.length !== 0 &&
              files.map((file) => (
                <FileListItem
                  key={file.file_id}
                  handleFilePreview={() => setCurrentFilePreviewId(file.file_id)}
                  handleDownload={() => downloadFile({dpsId: dpsId, dcId: dcId, fileId: file.file_id})}
                  markForDelete={() => setMarkedForDelete({ name: file.name, itemId: file.file_id })}
                  markForEdit={() => setMarkedForEdit({ name: file.name, fileId: file.file_id })}
                  {...file}
                />
              ))}
          </Stack>

          <Stack direction="row" justifyContent="center">
            <Pagination
              page={page}
              handlePageChange={handlePageChange}
              itemCount={total}
              itemsPerPage={pageSize}
              onItemsPerPageChange={setPageSize}
            />
          </Stack>
        </Stack>
      </Stack>
    </ContentLayout>
  );

  function useRefetch() {
    useEffect(() => {
      refetchFiles();
    }, [page, pageSize, search]);
  }
};